import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from '../../redux/actions/apiActions';
import PrimaryLayout from '../../layout/Layout';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Icon, Paper, Typography } from '@mui/material';
import { Download, ExpandMore, Menu, Fingerprint } from '@mui/icons-material';

import queueImage from '../../assets/images/landing/hero-5-img2.png';
import businessImage from '../../assets/images/landing/businesses_2.jpg';
import customerImage from '../../assets/images/landing/clients-e1418520215326.jpg';
import appStoreImage from '../../assets/images/landing/app_store.png';
import playStoreImage from '../../assets/images/landing/android_compressed.png';

import { useNavigate } from 'react-router-dom';
import { Config } from '../../config/regions';
import SearchBar from '../../components/SearchBar/SearchBar';

import './style.css';
import Footer from './Footer';
import SubscriptionPlans from './SubscriptionPlans';


const Landing = () => {

  const dispatch = useDispatch();
  const organizationList = useSelector(state => state.api.organizationList);
  const organizationListLoading = useSelector(state => state.api.organizationListLoading);
  const navigate = useNavigate();

  const loadCDNScript = (src, attributes = {}, onLoad) => {
    // Prevent duplicate loading
    if (document.querySelector(`script[src="${src}"]`)) {
      if (onLoad) onLoad();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    script.onload = onLoad;
    script.onerror = () => console.error(`Failed to load script: ${src}`);
    document.body.appendChild(script);
  }

  useEffect(() => {
    dispatch(fetchOrganizations());

    loadCDNScript(
      "https://cdn.que.zone/1.0/quezone-client.min.js?v=1.0.161",
      {
        "id": "quezone-script",
        "data-theme": '{"primary":"#d70001","secondary":"#ffffff","text":"#3a3a3a","gradientBg":["#ffffff","#d2d1d6"]}'
      },
      () => {
        console.log("Loaded Quezone CDN Scripts!");
        document.dispatchEvent(new Event("DOMContentLoaded", { bubbles: true }));
      }
    );

  }, [])


  const handleSearch = (e, selected) => {
    let orgRef = selected?.orgRef;
    if (orgRef) {
      navigate(`/${orgRef}`);
    }
  }

  const onClickRegister = () => {
    let onboardingUrl = Config.onboardingUrl + '/register';
    window.open(onboardingUrl);
  }
  const onClickLogin = () => {
    let onboardingUrl = Config.onboardingUrl + '/login';
    window.open(onboardingUrl);
  }
  const onClickJoinNow = () => {
    navigate("/p/organizations");
  }
  const onClickGetApp = (appType, platform) => {
    let url = '#';
    if (appType == "customer") {
      url = platform == "apple" ? "https://apps.apple.com/in/app/quezone/id1532363003" : "https://play.google.com/store/apps/details?id=com.custweb.quezone";
    } else if (appType == "business") {
      url = platform == "apple" ? "https://apps.apple.com/in/app/quezone-provider/id1572243889" : "https://play.google.com/store/apps/details?id=com.provider.quezone";
    }
    window.open(url);
  }
  const navigateToSegmentPage = (segment) => {
    navigate(`s/${segment}`);
  }

  const industrySegments = [
    { name: "Health & Wellness", icon: "volunteer_activism" },
    { name: "Fitness & Sports", icon: "fitness_center" },
    { name: "Professional Services", icon: "home_repair_service" },
    { name: "Business Operations", icon: "business_center" },
    { name: "Salon & Beauty", icon: "content_cut" },
    { name: "Spa", icon: "spa" },
    { name: "Education", icon: "school" },
    { name: "Tutoring Services", icon: "cast_for_education" },
    { name: "Automotive", icon: "airport_shuttle" },
    { name: "Pet Services", icon: "pets" },
    { name: "Government & Public Sector", icon: "public" },
    { name: "Home Services & Cleaning", icon: "home_work" },
  ]
  const features = [
    {
      icon: "event_available",
      title: "Intelligent Booking System",
      desc: "Our user-friendly online booking page is available 24/7, allowing customers to book appointments at their convenience. Real-time availability ensures no double-bookings and a smooth customer experience"
    },
    {
      icon: "redeem",
      title: "Package Bookings",
      desc: 'Offer combo deals and package offers, like \"5 Car Washes at a discounted rate." This helps you increase bookings and customer loyalty.',
    },
    {
      icon: "category",
      title: "Unique Product-Based Services",
      desc: 'Go beyond simple service booking. Quezone allows you to offer the same service, such as a "Car Wash," with different pricing and time allocations based on a "product" like "Small Hatchback," "Sedan," or "SUV." This flexible feature is perfect for businesses with variable pricing.',
    },
    {
      icon: "calendar_month",
      title: "Comprehensive Calendar Management",
      desc: "Easily view and manage all appointments and staff schedules in a single, intuitive calendar view (daily, weekly, or monthly). Sync seamlessly with external calendars like Google Calendar, iCal, and Outlook to keep your life organized",
    },
    {
      icon: "directions_walk",
      title: "Manage Walk-Ins (Offline) and Online Bookings Seamlessly",
      desc: "Don't let unexpected walk-in customers disrupt your daily schedule. With Quezone, your front desk can instantly generate offline tokens and add walk-in clients directly into your active queue alongside pre-booked online appointments. Keep your staff organized, manage live wait times, and provide every customer with an accurate ETA—whether they booked weeks in advance or just walked through the door."
    },
    {
      icon: "smartphone",
      title: "Got a smartphone? Your Front Desk, Upgraded.",
      desc: "Turn any Android or iOS device into a powerful queue management hub. Our dedicated mobile app empowers your team to register offline customers on the spot, issue instant tokens, and monitor real-time queue flow directly from the floor. Say goodbye to manual clipboards and chaotic waiting areas, and give your staff the tools they need to manage physical traffic effortlessly."
    },
    {
      icon: "schedule",
      title: "Real-Time Queue & ETA Monitoring",
      desc: "Provide your customers with clarity and transparency. Our system offers real-time monitoring to track the Estimated Time of Arrival (ETA) for each booking's token. This is especially helpful for clinics, as it allows patients to know exactly when to arrive, significantly reducing wait times.",
    },
    {
      icon: "notifications_active",
      title: "Advanced Reminder & Notification System",
      desc: <>Never miss a beat with multi-channel reminders for both customers and service providers. Customers get reminders via <b>SMS, email, app push notifications, or WhatsApp</b>. Providers get a daily report of their bookings and instant notifications for last-minute appointments.</>,
    },
    {
      icon: "devices",
      title: "Multiple Platform Support",
      desc: <>Access and manage your business from anywhere. Quezone is available on all major platforms, including <b>Web, Mobile, Android, and iOS</b>, giving you and your customers complete flexibility</>,
    },
    {
      icon: "integration_instructions",
      title: "Flexible Web Integration",
      desc: <>Quezone offers multiple options to integrate with your existing website. You can embed our <b>Iframe</b>, use our <b>Javascript CDN</b>, or install our <b>WordPress Plugin</b></>,
    },
    {
      icon: "verified_user",
      title: "Secure & Verified Bookings",
      desc: <>
        <b>OTP Verifications:</b> Every customer's phone number is verified with a one-time password (OTP) before a booking is confirmed, ensuring you get genuine inquiries. <br />
        <b>Customer Blacklisting:</b> Protect your business from fraudulent bookings and repeat no-shows by blacklisting customers.
      </>,
    },
    {
      icon: "headset_mic",
      title: "Virtual Meeting & Online Classes",
      desc: <>Seamlessly integrate with popular platforms like <b>Google Meet, Zoom, and Microsoft Teams</b>. This is ideal for scheduling video demos, virtual classrooms, and online training sessions.</>,
    },
    /*
    {
      icon: "",
      title: "",
      desc: "",
    },
    */
  ]
  const faq = [
    {
      question: "What is online booking system?",
      answer: "An online booking system allows the customers to book appointments with you or book your services through an online platform. Such a system allows the business owner/admin to control and oversee the availability of their services and the bookable hours that are available to the customers. An online scheduling software comes with an inbuilt reminder system that helps sending out automated email/SMS reminders to customers. Appointy is an advanced and free booking system that also allows the online booking portal to be customized to mirror your brand. ",
    },
    {
      question: "Does Quezone offer client and admin appointment scheduling apps?",
      answer: "Yes, we offer both. In the client app, clients can easily book, reschedule, or cancel appointments with you 24/7. In the admin app, the business owner/admin can stay on top of their business by keeping a check on their schedule – by creating, editing, or deleting appointments, setting bookable hours, checking booking history, running reports, etc."
    },
    {
      question: "How can I manage all the bookings that are made in the booking software?",
      answer: "You can have complete control over all the bookings that are made in the system. You can set flexible business hours at both the staff and service levels, set a limit on the number of bookings that can be made per client, and choose how much in advance clients can book, reschedule, or cancel their appointments."
    },
    {
      question: "Does Quezone also have a WordPress plugin?",
      answer: "Yes. Quezone extends its feature set to WordPress through its WordPress plugin."
    },
    {
      question: "Can I add Quezone to my website",
      answer: "Yes, Quezone can help you get more customers by letting you add an iFrame or button widget on your website. You can also integrate our js library or wordpress plugin to your website"
    },
    {
      question: "Can different staff members can have their own login?",
      answer: "Yes, Quezone lets your staff members manage their bookings and schedule by enabling separate staff logins. The system allows the admin to automatically track staff schedules, productivity, etc"
    },
    {
      question: "Can I integrate Whatsapp business account?",
      answer: "We provide whasapp chatbot service for booking and managing appointments. Your customers can book appointments through whatsapp once your whatsapp number is integrated"
    }
  ]


  const colors = {
    primary: "#d70001",//"#0066ff",//"#d70001",
    primaryBg: "#ffe6e6",//"#e6f0ff",//"#ffe6e6",
  }

  const commonStyles = {
    iconContainer: {
      my: 1,
      p: 1,
      mx: 'auto',
      backgroundColor: colors.primaryBg,
      width: 70,
      height: 70,
      borderRadius: 3,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  }

  return (
    <PrimaryLayout
      showQZLogo
      drawerIcon={<Menu />}
      showLocation={false}
      headerRightComponent={
        <Box display={'flex'} gap={1} sx={{ m: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/*<Button sx={{ textTransform: 'none' }} color="inherit" size="medium" href="#pricing"  >Pricing</Button>*/}
          <Button variant="outlined" sx={{ textTransform: 'none', display: { xs: "none", md: "inline-flex" } }} color="primary" size="medium" onClick={onClickLogin} startIcon={<Fingerprint />} target='_blank'> Business Login</Button>
          <Box
            onClick={onClickLogin}
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Fingerprint fontSize="small" />
            <Typography variant="caption">Business</Typography>
          </Box>
          <Button variant="contained" sx={{ textTransform: 'none', display: { xs: "none", md: "inline-flex" }, }} color="primary" size="medium" href="https://cdn.que.zone/public-assets/quezone-brochure.pdf" startIcon={<Download />} target='_blank'> Brochure</Button>
          <Box
            component="a"
            href="https://cdn.que.zone/public-assets/quezone-brochure.pdf"
            target="_blank"
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Download fontSize="small" />
            <Typography variant="caption">Brochure</Typography>
          </Box>
        </Box>
      }
    >
      <Paper elevation={0} className="texture-shattered"  >
        <GridContainer justifyContent="center" style={{ minHeight: '95vh' }}>
          <GridItem md={6} lg={4}>
            <GridContainer justifyContent="center" alignItems="center" style={{ height: '100%', padding: '10px 5px' }}>
              <GridItem>
                <Box
                  sx={{
                    p: 2,
                    animation: 'fadeIn 1s ease-in-out forwards', // Animation name, duration, and easing
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                    position: 'relative',
                    zIndex: 999
                  }}>
                  <Typography variant="h5" sx={{ fontWeight: '400' }} gutterBottom>
                    Your All-in-One Booking & Service Management Solution
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 1 }}>
                    Revolutionize Your Business with Quezone: The Smartest Way to Manage Services and Products.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                    Trusted by businesses globally and in India, Quezone is a powerful platform designed to streamline operations, manage product-based services, and ensure seamless communication with customers and staff.
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    sx={{ borderRadius: "8px", mr: 2, mt: 1 }}
                    href="#pricing"
                  >
                    Explore Plans
                  </Button>
                  <Button variant="outlined" color="error" size="large"
                    data-qz="QZ-IN-SERVICE-5f9fb0cd2ab79c0001a37fcf-"
                    onClick={window?.QuezoneJs?.onPressTriggerButton}
                    //href="/p/help-desk?subject=Request for Demo"
                    sx={{ mt: 1 }}>
                    Book a Demo
                  </Button>
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    14-Day Free Trial*
                  </Typography>
                  {/*
                  <SearchBar
                    containerProps={{ sx: { my: 5, zIndex: 9999 } }}
                    sx={{ mt: '5px' }}
                    cardProps={{
                      elevation: 5,
                      sx: { borderRadius: 5, p: 1.5, px: 3, maxWidth: 'md', mx: 'auto', zIndex: 999 }
                    }}
                    autoComplete
                    autoCompleteProps={{
                      sx: { flex: 1 },
                      options: organizationList.sort((a, b) => (a?.name?.toLowerCase()?.trim() > b?.name?.toLowerCase()?.trim() ? 1 : a?.name?.toLowerCase()?.trim() < b?.name?.toLowerCase()?.trim() ? -1 : 0)),
                      onChange: handleSearch,
                      getOptionLabel: (option) => option.name,
                      noOptionsText: "No matching organization"
                    }}
                    textFieldProps={{
                      placeholder: "Search for organization",
                      sx: { borderRadius: 5 },
                    }}
                    disableSticky={true}
                  />
                  */}
                </Box>
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem md={6} lg={4}>
            <GridContainer justifyContent="center" alignItems="center" style={{ height: '100%' }}>
              <Box sx={{
                animation: 'fadeInFromRight 1s ease-out', // Adjust the duration and easing function as needed
                '@keyframes fadeInFromRight': {
                  from: { opacity: 0, transform: 'translateX(100%)' },
                  to: { opacity: 1, transform: 'translateX(0%)' },
                },
              }}>
                <img src={queueImage} alt="Queue Image" className={""} />
              </Box>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper >
      {/* Features */}
      <Paper sx={{ py: 5, minHeight: '70vh' }} elevation={0}>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ p: 1, fontWeight: '700' }}>Quezone Features </Typography>
          <Typography variant="body" sx={{ opacity: 1 }}>Advanced booking features to help you put your business on autopilot</Typography>
        </Box>
        <GridContainer justifyContent="center" >
          <GridItem xs={12} md={10} lg={10} xl={8}>
            <GridContainer justifyContent="center" >
              {features.map((item, index) =>
                <GridItem xs={12} sm={12} md={6} lg={3} key={index} sx={{ display: "flex" }}>
                  <Card sx={{
                    p: 2, mx: 1, my: 5, textAlign: 'center',
                    borderRadius: 3,
                    flex: 1,              // make card fill parent
                    display: "flex",      // flex layout
                    flexDirection: "column",
                    animation: 'fadeInFromRight 0.25s ease-out', // Adjust the duration and easing function as needed
                    '@keyframes fadeInFromRight': {
                      from: { opacity: 0, transform: 'translateX(100%)' },
                      to: { opacity: 1, transform: 'translateX(0%)' },
                    },
                  }}>
                    <Box sx={commonStyles.iconContainer}>
                      <Icon sx={{ fontSize: 35, color: colors.primary }}>{item.icon}</Icon>
                    </Box>
                    <Typography variant="overline">{item.title}</Typography>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.desc}</Typography>
                    </Box>
                  </Card>
                </GridItem>
              )
              }
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper>
      {/* industry segments */}
      <Paper sx={{ py: 5, minHeight: '70vh' }} elevation={0} className="texture-shattered"   >
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ p: 1, fontWeight: '700' }}>Quezone can serve almost any industry segment </Typography>
          <Typography variant="body" sx={{ opacity: 1 }}>Our appointment booking system is fit for all service-based local businesses, multi-location enterprises, franchises, and more </Typography>
        </Box>
        <GridContainer justifyContent="center" >
          <GridItem xs={12} md={10} lg={10} xl={8}>
            <GridContainer justifyContent="space-between" >
              {industrySegments.map((item, index) =>
                <GridItem xs={6} sm={4} md={3} lg={2} key={index}>
                  <Box sx={{
                    my: 5, textAlign: 'center',
                    cursor: 'pointer',
                    animation: 'fadeInFromRight 0.25s ease-out', // Adjust the duration and easing function as needed
                    '@keyframes fadeInFromRight': {
                      from: { opacity: 0, transform: 'translateX(100%)' },
                      to: { opacity: 1, transform: 'translateX(0%)' },
                    },
                  }}
                    onClick={() => navigateToSegmentPage(item.name)}
                  >
                    <Box sx={commonStyles.iconContainer}>
                      <Icon sx={{ fontSize: 35, color: colors.primary }}>{item.icon}</Icon>
                    </Box>
                    <Typography variant="caption">{item.name}</Typography>
                  </Box>
                </GridItem>
              )
              }
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper>
      {/* FAQ */}
      <Paper sx={{ py: 5, minHeight: '70vh' }} elevation={0} >
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ p: 1, fontWeight: '700' }}>Frequently asked questions </Typography>
        </Box>
        <GridContainer justifyContent="center" >
          <GridItem xs={12} md={10} lg={10} xl={8}>
            <GridContainer justifyContent="space-between" >
              {faq.map((item, index) =>
                <GridItem xs={12} sm={12} md={6} key={index}>
                  <Box sx={{
                    m: 1,
                    animation: 'fadeInFromRight .25s ease-out', // Adjust the duration and easing function as needed
                    '@keyframes fadeInFromRight': {
                      from: { opacity: 0, transform: 'translateX(100%)' },
                      to: { opacity: 1, transform: 'translateX(0%)' },
                    },
                  }}>
                    <Accordion
                      sx={{ borderStyle: 'solid', borderColor: '#ddd', borderWidth: 1, borderRadius: "15px !important" }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                      >
                        <Typography>{item.question}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography sx={{ color: "text.secondary" }}>{item.answer}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </GridItem>
              )}
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper>
      {/* Pricing */}
      <Paper sx={{ py: 5, minHeight: '70vh' }} elevation={0} className="texture-shattered" id="pricing" >
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ p: 1, fontWeight: '700' }}>Business Solutions Plans & Pricing</Typography>
          <Typography variant="body" sx={{ opacity: 1 }}>Explore our flexible subscription options tailored to your business volume, with affordable base plans starting as low as ₹500 per month.</Typography>
        </Box>
        <SubscriptionPlans />
      </Paper>
      <Paper sx={{ py: 5, minHeight: '70vh' }} elevation={0} className="texture-shattered" id="pricing" >
        <Box sx={{ p: 5, textAlign: 'center', maxWidth: "md", margin: 'auto' }}>
          <Typography variant="h5" sx={{ p: 1, fontWeight: '700' }}>Book a Personalized Demo</Typography>
          <Typography variant="body" sx={{ opacity: 1 }}>Book a quick personalized demo with one of our experts to discover how we can transform your scheduling
            process.</Typography>
          <div data-quezone-widget="slot" data-qz="QZ-IN-SCHEDULE-071d50c1ae7bed6c293b89997f315e31-" data-theme='{"primary":"#d70001"}' data-config='{"hideService":true,"hideProvider":true,"hideLocation":true}'></div>
        </Box>

      </Paper>
      <Paper>
        <GridContainer justifyContent="center" style={{ backgroundColor: '#d7000180', minHeight: '15vh' }}>
        </GridContainer>
      </Paper>
      <Paper>
        <GridContainer justifyContent="center" style={{ minHeight: '70vh' }}>
          <GridItem xs={12} md={6} style={{ backgroundColor: '#f39c1180' }}>
            <GridContainer justifyContent="center" alignItems="center" style={{ padding: '3rem 0', height: '100%' }}>
              <GridItem>
                <Typography variant="h5" className="text-center"  >FOR CUSTOMERS </Typography>
                <img src={customerImage} alt="Customer Icon" className="h-56 w-56 rounded-full my-5 mx-auto" />
                <Typography variant="body1" >Getting in line will never be the same again </Typography>
                <GridContainer justifyContent="center" className="my-5">
                  <Button color="success" variant="contained" onClick={onClickJoinNow}>
                    JOIN A QUEUE
                  </Button>
                </GridContainer>
                <GridContainer justifyContent="space-around" style={{ padding: '1rem 0' }}>
                  <GridItem>
                    <img src={appStoreImage} alt="Appstore" width="100" onClick={() => onClickGetApp("customer", "apple")} />
                  </GridItem>
                  <GridItem>
                    <img src={playStoreImage} alt="Playstore" width="100" onClick={() => onClickGetApp("customer", "google")} />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem xs={12} md={6} style={{ backgroundColor: '#e84c3d80' }}>
            <GridContainer justifyContent="center" alignItems="center" style={{ padding: '3rem 0', height: '100%' }}>
              <GridItem>
                <Typography variant="h5" className="text-center">FOR BUSINESSES</Typography>
                <img src={businessImage} alt="Business Icon" className="h-56 w-56 rounded-full my-5 mx-auto" />
                <Typography variant="body1">Easy setup. Get started for free</Typography>
                <GridContainer justifyContent="center" className="my-5 gap-5">
                  <Button color="success" variant="contained" onClick={onClickRegister}>
                    REGISTER NOW
                  </Button>
                </GridContainer>
                <GridContainer justifyContent="space-around" style={{ padding: '1rem 0' }}>
                  <GridItem>
                    <img src={appStoreImage} alt="Appstore" width="100" onClick={() => onClickGetApp("business", "apple")} />
                  </GridItem>
                  <GridItem>
                    <img src={playStoreImage} alt="Playstore" width="100" onClick={() => onClickGetApp("business", "google")} />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Paper>
      {/* Footer */}
      <Footer />
    </PrimaryLayout >
  )
}

export default Landing; 